'use client';

import React, { useId, useState } from 'react';
import { Button } from './button';
import { Section } from './section';
import { Sheet } from './sheet';
import { Collapsible } from './collapsible';
import { WorkList } from './work-list';
import { useCompositionDensity } from './composition-density';
import { CountedAttentionQueue } from './counted-attention-queue';
import type { AttentionItem } from './attention-model';

export interface AttentionQueueItem {
  id: string;
  title: string;
  reason: React.ReactNode;
  /** Catalogue badge supplied by the owning workflow. */
  badge?: React.ReactNode;
  action: React.ReactNode;
  overflow?: React.ReactNode;
  protected?: boolean;
}
export interface AttentionQueueProps {
  items: readonly AttentionQueueItem[];
  layout?: 'work' | 'record';
  title?: string;
  limit?: number;
  /** Caller owns persisted hidden rows; protected IDs are always ignored. */
  hiddenIds?: readonly string[];
  onHiddenChange?: (ids: string[]) => void;
  alsoOpen?: React.ReactNode;
}

export function AttentionQueue({
  items,
  layout = 'work',
  title = 'Needs action',
  limit = 3,
  hiddenIds = [],
  onHiddenChange,
  alsoOpen,
}: AttentionQueueProps) {
  const [showAll, setShowAll] = useState(false);
  const [customise, setCustomise] = useState(false);
  const listId = useId();
  const density = useCompositionDensity();
  const hidden = items.filter((item) => !item.protected && hiddenIds.includes(item.id));
  const eligible = items.filter((item) => item.protected || !hiddenIds.includes(item.id));
  const byId = new Map(eligible.map((item) => [item.id, item]));
  const countedItems: AttentionItem[] = eligible.map((item) => ({
    id: item.id,
    title: item.title,
    reason: '',
    severity: 'routine',
    count: 1,
    protected: item.protected,
    action: { label: item.title },
  }));
  if (items.length === 0 && alsoOpen == null) return null;
  return (
    <CountedAttentionQueue
      items={countedItems}
      title={title}
      chrome={{
        renderRows: (visible) => {
          const ordered = visible.map((item) => byId.get(item.id)!).filter(Boolean);
          const protectedItems = ordered.filter((item) => item.protected);
          const ordinary = ordered.filter((item) => !item.protected);
          const cap = Math.max(0, Math.floor(limit) || 0);
          const shown = [
            ...protectedItems,
            ...(showAll ? ordinary : ordinary.slice(0, Math.max(0, cap - protectedItems.length))),
          ];
          return (
            <>
              <div id={listId}>
                {layout === 'record' ? (
                  <ul aria-label={title} className="min-w-0 divide-y divide-border">
                    {shown.map((item) => (
                      <li
                        key={item.id}
                        className="flex min-w-0 flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-4"
                        data-attention-row
                      >
                        {item.badge != null && (
                          <div className="shrink-0 self-start">{item.badge}</div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-body text-body font-semibold text-text-heading">
                            {item.title}
                          </p>
                          <span className="sr-only">
                            {item.protected ? 'Protected' : 'Needs action'}
                          </span>
                          <div className="font-body text-caption text-text-muted">
                            {item.reason}
                          </div>
                        </div>
                        <div
                          className="flex min-w-0 flex-wrap gap-2 sm:shrink-0 [&>a]:inline-flex [&>a]:min-h-11 [&>a]:items-center [&>button]:min-h-11"
                          data-attention-action
                        >
                          {item.action}
                          {item.overflow}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <WorkList
                    label={title}
                    rows={shown.map((item) => ({
                      id: item.id,
                      label: item.title,
                      identity: item.title,
                      state: item.protected ? 'Protected' : 'Needs action',
                      reason: item.reason,
                      action: item.action,
                      overflow: item.overflow,
                    }))}
                  />
                )}
              </div>
              {(showAll || ordered.length > shown.length) && (
                <Button
                  type="button"
                  variant="tertiary"
                  density={density}
                  aria-expanded={showAll}
                  aria-controls={listId}
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? 'Show fewer' : `Show all ${ordered.length}`}
                </Button>
              )}
            </>
          );
        },
        wrap: ({ rows }) => (
          <Section
            title={title}
            actions={
              layout === 'record' && !onHiddenChange ? undefined : (
                <Button
                  type="button"
                  variant="tertiary"
                  density={density}
                  onClick={() => setCustomise(true)}
                >
                  Customise · {hidden.length} hidden
                </Button>
              )
            }
          >
            {rows}
            {alsoOpen != null && <Collapsible label="Also open">{alsoOpen}</Collapsible>}
            <Sheet
              open={customise}
              onClose={() => setCustomise(false)}
              title="Customise needs action"
              description={`${hidden.length} hidden`}
              footer={
                <Button
                  type="button"
                  variant="secondary"
                  density={density}
                  disabled={!onHiddenChange}
                  onClick={() => onHiddenChange?.([])}
                >
                  Reset layout
                </Button>
              }
            >
              {items.map((item) => (
                <label
                  key={item.id}
                  className="flex min-h-11 min-w-0 items-center gap-2 font-body text-body"
                >
                  <input
                    type="checkbox"
                    className="size-4 shrink-0 accent-action"
                    checked={item.protected || !hiddenIds.includes(item.id)}
                    disabled={item.protected || !onHiddenChange}
                    onChange={(event) => {
                      if (item.protected) return;
                      onHiddenChange?.(
                        event.target.checked
                          ? hiddenIds.filter((id) => id !== item.id)
                          : [...hiddenIds, item.id],
                      );
                    }}
                  />
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                    {item.title}
                    {item.protected && ' · Protected'}
                  </span>
                </label>
              ))}
            </Sheet>
          </Section>
        ),
      }}
    />
  );
}
